import { useContext, useEffect, useState } from 'react';
import { useForm, UseFormTrigger, UseFormWatch } from 'react-hook-form';
import { BaseProcessStep } from '@components/shared/base-process/types';
import { STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { Admission } from '@domain/admission';
import { ROLE_PROMOTER, ROLE_TEAM_LEADER } from '@domain/company';
import { Event } from '@domain/event';
import { useGetAdmission } from '@hooks/admissions/use-get-admission';
import { useCurrentEvent } from '@hooks/events/use-current-event';
import { useGetEvent } from '@hooks/events/use-get-event';
import { UseQueryResult } from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';

import { ProcessFormFields, UseProcessFormConfig, UseProcessFormReturn } from './use-process-form.types';

// Types for helper function return values
interface ExtendedFields {
  addOns?: string[];
  saleType?: string;
  multiOfferText?: string;
  bookingFormFields?: Record<string, unknown>;
  ticketHolderFormFields?: unknown[];
}

interface InviteGuestFields {
  invitationId?: string;
  type?: string;
  infoType?: string;
  multiInviteText?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  gender?: string;
  nationality?: string;
  birth?: string;
}

interface BaseFields {
  methods: ReturnType<typeof useForm<ProcessFormFields>>;
  offerId: string;
  eventId: string;
  channelId: string;
  quantity: number;
  step: BaseProcessStep;
  setStep: (step: BaseProcessStep) => void;
  closeModal: () => void;
  offer: UseQueryResult<Admission, RequestError>['data'];
  event: UseQueryResult<Event, RequestError>['data'];
  triggerValidation: UseFormTrigger<ProcessFormFields>;
}

// Helper function to build extended defaults
const buildExtendedDefaults = (config: UseProcessFormConfig): Partial<ProcessFormFields> => {
  const extendedDefaults: Partial<ProcessFormFields> = {};

  if (config.includeAddOns) {
    extendedDefaults.addOns = [];
  }

  if (config.includeSaleType) {
    extendedDefaults.saleType = '';
  }

  if (config.includeMultiText) {
    extendedDefaults.multiOfferText = '';
  }

  if (config.includeBookingFormFields) {
    extendedDefaults.bookingFormFields = {};
  }

  if (config.includeTicketHolderFormFields) {
    extendedDefaults.ticketHolderFormFields = [];
  }

  if (config.includeInvitationFields) {
    extendedDefaults.invitationId = '';
    extendedDefaults.type = '';
    extendedDefaults.infoType = '';
    extendedDefaults.multiInviteText = '';
  }

  if (config.includeGuestInfo) {
    extendedDefaults.email = '';
    extendedDefaults.firstName = '';
    extendedDefaults.lastName = '';
    extendedDefaults.phone = '';
    extendedDefaults.gender = '';
    extendedDefaults.nationality = '';
    extendedDefaults.birth = '';
  }

  return extendedDefaults;
};

// Helper function to build base defaults
const buildBaseDefaults = (contexts: {
  isLiveEvent?: boolean;
  paramEventId?: string;
  defaultChannelId: string;
  isTeamLeaderOrPromoter: boolean;
}): ProcessFormFields => {
  const { isLiveEvent, paramEventId, defaultChannelId, isTeamLeaderOrPromoter } = contexts;

  return {
    eventId: isLiveEvent ? paramEventId ?? '' : '',
    offerId: '',
    channelId: isTeamLeaderOrPromoter ? '' : defaultChannelId,
    quantity: 1,
  };
};

// Helper function to get watched extended fields
const getWatchedExtendedFields = (
  config: UseProcessFormConfig,
  watch: UseFormWatch<ProcessFormFields>
): ExtendedFields => {
  return {
    addOns: config.includeAddOns ? watch('addOns') : undefined,
    saleType: config.includeSaleType ? watch('saleType') : undefined,
    multiOfferText: config.includeMultiText ? watch('multiOfferText') : undefined,
    bookingFormFields: config.includeBookingFormFields ? watch('bookingFormFields') : undefined,
    ticketHolderFormFields: config.includeTicketHolderFormFields ? watch('ticketHolderFormFields') : undefined,
  };
};

// Helper function to get watched invite guest fields
const getWatchedInviteGuestFields = (
  config: UseProcessFormConfig,
  watch: UseFormWatch<ProcessFormFields>
): InviteGuestFields => {
  return {
    invitationId: config.includeInvitationFields ? watch('invitationId') : undefined,
    type: config.includeInvitationFields ? watch('type') : undefined,
    infoType: config.includeInvitationFields ? watch('infoType') : undefined,
    multiInviteText: config.includeInvitationFields ? watch('multiInviteText') : undefined,
    email: config.includeGuestInfo ? watch('email') : undefined,
    firstName: config.includeGuestInfo ? watch('firstName') : undefined,
    lastName: config.includeGuestInfo ? watch('lastName') : undefined,
    phone: config.includeGuestInfo ? watch('phone') : undefined,
    gender: config.includeGuestInfo ? watch('gender') : undefined,
    nationality: config.includeGuestInfo ? watch('nationality') : undefined,
    birth: config.includeGuestInfo ? watch('birth') : undefined,
  };
};

// Helper function to build return object with conditional fields
const buildReturnObject = (
  config: UseProcessFormConfig,
  baseFields: BaseFields,
  extendedFields: ExtendedFields,
  inviteGuestFields: InviteGuestFields
) => {
  return {
    ...baseFields,
    // Extended fields (only included if configured)
    ...(config.includeAddOns && { addOns: extendedFields.addOns }),
    ...(config.includeSaleType && { saleType: extendedFields.saleType }),
    ...(config.includeMultiText && { multiOfferText: extendedFields.multiOfferText }),
    ...(config.includeBookingFormFields && { bookingFormFields: extendedFields.bookingFormFields }),
    ...(config.includeTicketHolderFormFields && { ticketHolderFormFields: extendedFields.ticketHolderFormFields }),
    // Invite guest specific fields
    ...(config.includeInvitationFields && {
      invitationId: inviteGuestFields.invitationId,
      type: inviteGuestFields.type,
      infoType: inviteGuestFields.infoType,
      multiInviteText: inviteGuestFields.multiInviteText,
    }),
    ...(config.includeGuestInfo && {
      email: inviteGuestFields.email,
      firstName: inviteGuestFields.firstName,
      lastName: inviteGuestFields.lastName,
      phone: inviteGuestFields.phone,
      gender: inviteGuestFields.gender,
      nationality: inviteGuestFields.nationality,
      birth: inviteGuestFields.birth,
    }),
  };
};

export const useProcessForm = (config: UseProcessFormConfig = {}): UseProcessFormReturn => {
  // Context values
  const { event: currentEvent } = useCurrentEvent();
  const { id: paramEventId, isLiveEvent } = currentEvent ?? {};

  const {
    companyState: {
      defaultChannel: { id: defaultChannelId },
    },
  } = useContext(CompanyClubContext);

  const { setPreventExit, toggleModal } = useContext(ModalContext);
  const { role } = useContext(CompanyAuthContext);

  const isTeamLeaderOrPromoter = Boolean(role && (role.name === ROLE_TEAM_LEADER || role.name === ROLE_PROMOTER));

  // Build default values
  const buildDefaultValues = (): ProcessFormFields => {
    const baseDefaults = buildBaseDefaults({
      isLiveEvent,
      paramEventId,
      defaultChannelId,
      isTeamLeaderOrPromoter,
    });

    const extendedDefaults = buildExtendedDefaults(config);

    return {
      ...baseDefaults,
      ...extendedDefaults,
      ...config.additionalDefaults,
    };
  };

  // Form setup
  const methods = useForm<ProcessFormFields>({
    defaultValues: buildDefaultValues(),
    mode: config.validationMode === 'manual' ? 'onSubmit' : 'onChange',
  });

  const {
    watch,
    trigger,
    formState: { isDirty },
  } = methods;

  // Base fields (always available)
  const offerId = watch('offerId');
  const eventId = watch('eventId');
  const channelId = watch('channelId');
  const quantity = watch('quantity');

  // Get watched fields using helper functions
  const extendedFields = getWatchedExtendedFields(config, watch);
  const inviteGuestFields = getWatchedInviteGuestFields(config, watch);

  // Data fetching
  const effectiveOfferId = (config.includeInvitationFields ? inviteGuestFields.invitationId : offerId) ?? '';
  const { data: offer } = useGetAdmission({
    offerId: effectiveOfferId,
    channelId,
    options: {
      enabled: Boolean(effectiveOfferId),
    },
  });
  const { data: event } = useGetEvent({ eventId });

  // Step management
  const [step, setStep] = useState<BaseProcessStep>(STEP_SELECT_TYPE);

  // Modal close functionality
  const closeModal = () => {
    setPreventExit(false);
    toggleModal({ type: '', isOpen: false });
  };

  // Prevent exit handling - don't prevent exit on success step
  useEffect(() => {
    const shouldPreventExit = isDirty && step !== STEP_SUCCESS;
    setPreventExit(shouldPreventExit);
    return () => setPreventExit(false);
  }, [isDirty, step, setPreventExit]);

  // Build and return the result object
  const baseFields: BaseFields = {
    methods,
    offerId,
    eventId,
    channelId,
    quantity,
    step,
    setStep,
    closeModal,
    offer,
    event,
    triggerValidation: trigger,
  };

  return buildReturnObject(config, baseFields, extendedFields, inviteGuestFields);
};
